import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { classApi } from '../../../api/classes';
import { formatCurrencyFromMinor } from '../../../utils/currency';
import ClosedSchoolNotice from './ClosedSchoolNotice';
import './PublicSchool.css';
import './PublicGroupPage.css';

/**
 * PublicGroupPage - Public group/program page
 * URL: /school/:orgSlug/groups/:groupSlug
 */
const PublicGroupPage = () => {
    const { orgSlug, groupSlug } = useParams();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await classApi.getPublicGroupPage(orgSlug, groupSlug);
                if (r.ok) {
                    setData(r.data);
                } else {
                    setNotFound(true);
                }
            } catch {
                setNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [orgSlug, groupSlug]);

    if (isLoading) {
        return <div className="public-school"><p className="public-school-loading">Loading...</p></div>;
    }

    if (notFound || !data) {
        return (
            <div className="public-school">
                <div className="public-school-not-found">
                    <h2>Page Not Found</h2>
                    <p>This program page does not exist or has been removed.</p>
                    <Link to={`/school/${orgSlug}`} className="public-group-back-link">Back to school</Link>
                </div>
            </div>
        );
    }

    // Class management switched off by the school: header + closed notice, no listings.
    if (data.isOpen === false) {
        return <ClosedSchoolNotice orgSlug={orgSlug} orgName={data.orgName} branding={data.branding} />;
    }

    const { group, classes, shortCourses, school } = data;
    const b = school?.branding || {};
    const headerColor = b.headerColor || '#451f78';
    const headerTextColor = b.headerTextColor || '#ffffff';
    const backgroundColor = b.backgroundColor || '#f5f0fb';
    const textColor = b.textColor || '#333333';
    const buttonColor = b.buttonColor || '#451f78';
    const buttonTextColor = b.buttonTextColor || '#ffffff';

    const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="public-school" style={{ background: backgroundColor, color: textColor }}>
            <div className="public-school-header public-school-header--detail" style={{ background: headerColor }}>
                <Link to={`/school/${orgSlug}`} className="public-group-back-link" style={{ color: headerTextColor, opacity: 0.75 }}>
                    {school?.name || 'School'}
                </Link>
                <h1 className="public-school-title" style={{ color: headerTextColor }}>{group.name}</h1>
                {group.description && (
                    <p className="public-school-subtitle" style={{ color: headerTextColor }}>{group.description}</p>
                )}
            </div>

            <div className="public-school-body">
                {classes?.length > 0 ? (
                    <>
                        <p className="public-school-section-title">Available Classes</p>
                        <div className="public-group-classes-list">
                            {classes.map(cls => (
                                <div key={cls.id} className="public-group-class-card"
                                    style={{ borderColor: headerColor + '33' }}>
                                    <div className="public-group-class-info">
                                        <p className="public-group-class-name" style={{ color: headerColor }}>{cls.name}</p>
                                        <p className="public-group-class-meta">
                                            {cls.style} · {cls.level}
                                        </p>
                                        <p className="public-group-class-time">
                                            {DAY_LABELS[cls.dayOfWeek] || ''} {cls.startTime}
                                            {cls.durationMins ? ` · ${cls.durationMins} min` : ''}
                                        </p>
                                        {cls.price > 0 && (
                                            <p className="public-group-class-price">
                                                {formatCurrencyFromMinor(cls.price)} per class
                                            </p>
                                        )}
                                    </div>
                                    <Link
                                        to={`/school/${orgSlug}/enquire?classId=${cls.id}&className=${encodeURIComponent(cls.name)}`}
                                        className="public-group-enrol-btn"
                                        style={{ background: buttonColor, color: buttonTextColor }}
                                    >
                                        Enquire
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="public-school-loading" style={{ textAlign: 'left', paddingLeft: 0 }}>
                        No classes currently available. Check back soon.
                    </p>
                )}

                {shortCourses?.length > 0 && (
                    <>
                        <p className="public-school-section-title">Short Courses</p>
                        <div className="public-group-classes-list">
                            {shortCourses.map(sc => (
                                <div key={sc.id} className="public-group-class-card"
                                    style={{ borderColor: headerColor + '33' }}>
                                    <div className="public-group-class-info">
                                        <p className="public-group-class-name" style={{ color: headerColor }}>{sc.name}</p>
                                        <p className="public-group-class-meta">
                                            {sc.style}{sc.level ? ` · ${sc.level}` : ''}
                                        </p>
                                        {sc.sessionCount && (
                                            <p className="public-group-class-time">{sc.sessionCount} sessions{sc.startTime ? ` · ${DAY_LABELS[sc.dayOfWeek] || ''} ${sc.startTime}` : ''}</p>
                                        )}
                                        {sc.startDate && (
                                            <p className="public-group-class-time">
                                                Starts {new Date(sc.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                                            </p>
                                        )}
                                    </div>
                                    <Link
                                        to={`/school/${orgSlug}/enquire?classId=${sc.id}&className=${encodeURIComponent(sc.name)}${sc.restrictLeaders ? '&pauseLeaders=1' : ''}${sc.restrictFollowers ? '&pauseFollowers=1' : ''}`}
                                        className="public-group-enrol-btn"
                                        style={{ background: buttonColor, color: buttonTextColor }}
                                    >
                                        Enquire
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <Link
                    to={`/school/${orgSlug}/enquire`}
                    className="public-school-enquire-btn"
                    style={{ background: buttonColor, color: buttonTextColor }}
                >
                    General Enquiry
                </Link>
            </div>
            <p className="public-school-powered-by">Powered by Dance Localé</p>
        </div>
    );
};

export default PublicGroupPage;
